/**
 * Document Form Component
 * Form for filling in template placeholders and generating documents
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Save,
  Eye,
  AlertCircle,
  Check,
  Loader,
  Globe,
  Calendar,
  Mail,
  Type,
  Hash,
  List,
  HelpCircle,
  Wand2
} from 'lucide-react';
import { Card, CardHeader, CardBody, CardFooter } from '../../common/Card';
import { Button } from '../../common/Button';
import { Input } from '../../common/Input';
import { useAuth } from '../../../contexts/AuthContext';
import { documentService, documentAIService } from '../../../services/api/documentGeneratorService';
import {
  DocumentTemplate,
  Placeholder,
  PlaceholderType,
  GenerateDocumentInput,
  ConfidentialityLevel,
  SUPPORTED_JURISDICTIONS,
  CONFIDENTIALITY_LABELS
} from '../../../types/documentGenerator.types';

interface DocumentFormProps {
  template: DocumentTemplate;
  onSuccess?: (documentId: string) => void;
  onCancel?: () => void;
}

interface FormValues {
  title: string;
  jurisdiction: string;
  effectiveDate: string;
  expirationDate: string;
  confidentialityLevel: ConfidentialityLevel;
  notes: string;
  tags: string[];
  placeholderValues: Record<string, any>;
}

const placeholderIcons: Record<PlaceholderType, React.ReactNode> = {
  text: <Type className="w-4 h-4" />,
  textarea: <FileText className="w-4 h-4" />,
  date: <Calendar className="w-4 h-4" />,
  number: <Hash className="w-4 h-4" />,
  email: <Mail className="w-4 h-4" />,
  select: <List className="w-4 h-4" />
};

export const DocumentForm: React.FC<DocumentFormProps> = ({
  template,
  onSuccess,
  onCancel
}) => {
  const { token } = useAuth();
  const [formValues, setFormValues] = useState<FormValues>({
    title: '',
    jurisdiction: template.defaultJurisdiction || 'US-DE',
    effectiveDate: '',
    expirationDate: '',
    confidentialityLevel: 'internal',
    notes: '',
    tags: [],
    placeholderValues: {}
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState<string | null>(null);

  // Initialize placeholder values with defaults
  useEffect(() => {
    const initialValues: Record<string, any> = {};
    template.placeholders.forEach(placeholder => {
      if (placeholder.defaultValue) {
        initialValues[placeholder.key] = placeholder.defaultValue;
      } else if (placeholder.type === 'date') {
        initialValues[placeholder.key] = '';
      } else if (placeholder.type === 'number') {
        initialValues[placeholder.key] = '';
      } else {
        initialValues[placeholder.key] = '';
      }
    });
    setFormValues(prev => ({
      ...prev,
      placeholderValues: initialValues,
      title: `${template.name} - ${new Date().toLocaleDateString()}`
    }));
  }, [template]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formValues.title.trim()) {
      newErrors.title = 'Document title is required';
    }

    // Validate required placeholders
    template.placeholders.forEach(placeholder => {
      if (placeholder.required) {
        const value = formValues.placeholderValues[placeholder.key];
        if (!value || (typeof value === 'string' && !value.trim())) {
          newErrors[placeholder.key] = `${placeholder.label} is required`;
        }
      }

      // Validate patterns if specified
      if (placeholder.validation?.pattern) {
        const value = formValues.placeholderValues[placeholder.key];
        if (value && !new RegExp(placeholder.validation.pattern).test(value)) {
          newErrors[placeholder.key] = `Invalid format for ${placeholder.label}`;
        }
      }

      // Validate min/max for numbers
      if (placeholder.type === 'number' && placeholder.validation) {
        const value = Number(formValues.placeholderValues[placeholder.key]);
        if (placeholder.validation.min !== undefined && value < placeholder.validation.min) {
          newErrors[placeholder.key] = `${placeholder.label} must be at least ${placeholder.validation.min}`;
        }
        if (placeholder.validation.max !== undefined && value > placeholder.validation.max) {
          newErrors[placeholder.key] = `${placeholder.label} must be at most ${placeholder.validation.max}`;
        }
      }

      // Validate min/max length for text
      if ((placeholder.type === 'text' || placeholder.type === 'textarea') && placeholder.validation) {
        const value = formValues.placeholderValues[placeholder.key] || '';
        if (placeholder.validation.minLength !== undefined && value.length < placeholder.validation.minLength) {
          newErrors[placeholder.key] = `${placeholder.label} must be at least ${placeholder.validation.minLength} characters`;
        }
        if (placeholder.validation.maxLength !== undefined && value.length > placeholder.validation.maxLength) {
          newErrors[placeholder.key] = `${placeholder.label} must be at most ${placeholder.validation.maxLength} characters`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formValues, template.placeholders]);

  const handlePlaceholderChange = (key: string, value: any) => {
    setFormValues(prev => ({
      ...prev,
      placeholderValues: {
        ...prev.placeholderValues,
        [key]: value
      }
    }));

    // Clear error for this field
    if (errors[key]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const handlePreview = async () => {
    if (!token) return;

    setLoadingPreview(true);
    try {
      const result = await documentService.preview(
        token,
        template._id,
        formValues.placeholderValues,
        formValues.jurisdiction
      );
      setPreviewContent(result.content);
      setShowPreview(true);
    } catch (err: any) {
      setErrors(prev => ({ ...prev, preview: err.message || 'Failed to generate preview' }));
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleAISuggest = async (placeholder: Placeholder) => {
    if (!token) return;

    setAiSuggesting(placeholder.key);
    try {
      const context = `Template: ${template.name}\nCategory: ${template.category}\nPlaceholder: ${placeholder.label}\nType: ${placeholder.type}`;
      const result = await documentAIService.improveContent(
        token,
        placeholder.helpText || `Suggest a value for ${placeholder.label}`,
        'professional',
        context
      );
      handlePlaceholderChange(placeholder.key, result.improved);
    } catch (err) {
      console.error('AI suggestion failed:', err);
    } finally {
      setAiSuggesting(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !validateForm()) return;

    setSubmitting(true);
    try {
      const input: GenerateDocumentInput = {
        templateId: template._id,
        title: formValues.title,
        filledValues: formValues.placeholderValues,
        jurisdiction: formValues.jurisdiction,
        effectiveDate: formValues.effectiveDate || undefined,
        expirationDate: formValues.expirationDate || undefined,
        confidentialityLevel: formValues.confidentialityLevel,
        notes: formValues.notes || undefined,
        tags: formValues.tags.length > 0 ? formValues.tags : undefined
      };

      const document = await documentService.generate(token, input);
      onSuccess?.(document._id);
    } catch (err: any) {
      setErrors(prev => ({ ...prev, submit: err.message || 'Failed to generate document' }));
    } finally {
      setSubmitting(false);
    }
  };

  const renderPlaceholderInput = (placeholder: Placeholder) => {
    const value = formValues.placeholderValues[placeholder.key] || '';
    const error = errors[placeholder.key];
    const isAiSuggesting = aiSuggesting === placeholder.key;

    const baseInputClass = `w-full ${error ? 'border-red-500' : ''}`;

    switch (placeholder.type) {
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handlePlaceholderChange(placeholder.key, e.target.value)}
            className={`${baseInputClass} rounded-lg border border-gray-300 dark:border-gray-600 bg-surface-base text-text-primary px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
          >
            <option value="">Select {placeholder.label}</option>
            {placeholder.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handlePlaceholderChange(placeholder.key, e.target.value)}
            rows={4}
            className={`${baseInputClass} rounded-lg border border-gray-300 dark:border-gray-600 bg-surface-base text-text-primary px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none`}
            placeholder={placeholder.helpText || `Enter ${placeholder.label.toLowerCase()}`}
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handlePlaceholderChange(placeholder.key, e.target.value)}
            className={baseInputClass}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => handlePlaceholderChange(placeholder.key, e.target.value)}
            min={placeholder.validation?.min}
            max={placeholder.validation?.max}
            className={baseInputClass}
            placeholder={placeholder.helpText || `Enter ${placeholder.label.toLowerCase()}`}
          />
        );

      case 'email':
        return (
          <Input
            type="email"
            value={value}
            onChange={(e) => handlePlaceholderChange(placeholder.key, e.target.value)}
            className={baseInputClass}
            placeholder={placeholder.helpText || 'email@example.com'}
          />
        );

      default:
        return (
          <div className="flex gap-2">
            <Input
              type="text"
              value={value}
              onChange={(e) => handlePlaceholderChange(placeholder.key, e.target.value)}
              className={`${baseInputClass} flex-1`}
              placeholder={placeholder.helpText || `Enter ${placeholder.label.toLowerCase()}`}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => handleAISuggest(placeholder)}
              disabled={isAiSuggesting}
              title="AI Suggest"
            >
              {isAiSuggesting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4" />
              )}
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Template Info */}
      <Card variant="premium" padding="md">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-primary-100 dark:bg-primary-900/30">
            <FileText className="w-6 h-6 text-primary-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-text-primary">{template.name}</h2>
            <p className="text-text-secondary mt-1">{template.description}</p>
          </div>
        </div>
      </Card>

      <form onSubmit={handleSubmit}>
        {/* Document Details */}
        <Card className="mb-6">
          <CardHeader title="Document Details" subtitle="Basic information about the document" />
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Document Title <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formValues.title}
                  onChange={(e) => setFormValues(prev => ({ ...prev, title: e.target.value }))}
                  className={errors.title ? 'border-red-500' : ''}
                  placeholder="Enter document title"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Jurisdiction
                </label>
                <select
                  value={formValues.jurisdiction}
                  onChange={(e) => setFormValues(prev => ({ ...prev, jurisdiction: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-surface-base text-text-primary px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {SUPPORTED_JURISDICTIONS.map(j => (
                    <option key={j.code} value={j.code}>{j.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Confidentiality Level
                </label>
                <select
                  value={formValues.confidentialityLevel}
                  onChange={(e) => setFormValues(prev => ({ ...prev, confidentialityLevel: e.target.value as ConfidentialityLevel }))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-surface-base text-text-primary px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {Object.entries(CONFIDENTIALITY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Effective Date
                </label>
                <Input
                  type="date"
                  value={formValues.effectiveDate}
                  onChange={(e) => setFormValues(prev => ({ ...prev, effectiveDate: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Expiration Date
                </label>
                <Input
                  type="date"
                  value={formValues.expirationDate}
                  onChange={(e) => setFormValues(prev => ({ ...prev, expirationDate: e.target.value }))}
                />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Placeholder Fields */}
        {template.placeholders.length > 0 && (
          <Card className="mb-6">
            <CardHeader
              title="Template Fields"
              subtitle="Fill in the required information for the document"
            />
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {template.placeholders.map(placeholder => (
                  <div
                    key={placeholder.key}
                    className={placeholder.type === 'textarea' ? 'md:col-span-2' : ''}
                  >
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      <span className="inline-flex items-center gap-1.5">
                        {placeholderIcons[placeholder.type]}
                        {placeholder.label}
                        {placeholder.required && <span className="text-red-500">*</span>}
                        {placeholder.helpText && (
                          <span title={placeholder.helpText}>
                            <HelpCircle className="w-3.5 h-3.5 text-text-tertiary" />
                          </span>
                        )}
                      </span>
                    </label>
                    {renderPlaceholderInput(placeholder)}
                    {errors[placeholder.key] && (
                      <p className="mt-1 text-sm text-red-500">{errors[placeholder.key]}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Notes */}
        <Card className="mb-6">
          <CardHeader title="Additional Notes" subtitle="Optional notes for this document" />
          <CardBody>
            <textarea
              value={formValues.notes}
              onChange={(e) => setFormValues(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-surface-base text-text-primary px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              placeholder="Add any additional notes..."
            />
          </CardBody>
        </Card>

        {/* Error Messages */}
        {(errors.preview || errors.submit) && (
          <div className="flex items-center gap-2 p-4 mb-6 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{errors.preview || errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between gap-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handlePreview}
            disabled={loadingPreview}
            className="gap-2"
          >
            {loadingPreview ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            Preview
          </Button>

          <div className="flex items-center gap-3">
            {onCancel && (
              <Button type="button" variant="secondary" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={submitting} className="gap-2">
              {submitting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Generate Document
            </Button>
          </div>
        </div>
      </form>

      {/* Preview Modal */}
      {showPreview && previewContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <CardHeader
              title="Document Preview"
              action={
                <Button variant="secondary" size="sm" onClick={() => setShowPreview(false)}>
                  Close
                </Button>
              }
            />
            <CardBody className="flex-1 overflow-auto">
              <div
                className="prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: previewContent.replace(/\n/g, '<br>') }}
              />
            </CardBody>
            <CardFooter>
              <Button onClick={() => setShowPreview(false)}>
                <Check className="w-4 h-4 mr-2" />
                Close Preview
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DocumentForm;
